package com.heelion.farmafreelance.ui

import androidx.compose.animation.core.Animatable
import androidx.compose.foundation.Image
import androidx.compose.foundation.border
import com.heelion.farmafreelance.FarmaFreelanceBlue
import farmafreelance.composeapp.generated.resources.Res
import farmafreelance.composeapp.generated.resources.farmyno00
import org.jetbrains.compose.resources.painterResource
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.platform.LocalFocusManager
import com.heelion.farmafreelance.getPlatform
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import com.heelion.farmafreelance.agent.RispostaAgente
import com.heelion.farmafreelance.agent.TipoDomanda
import com.heelion.farmafreelance.agent.TipoPromemoria
import com.heelion.farmafreelance.voice.VoiceIntent
import com.heelion.farmafreelance.voice.rememberVoiceRecognizer
import kotlin.math.roundToInt

/**
 * Posizione del pulsante trascinato dall'utente, come spostamento (px) dalla posizione standard
 * in basso a destra. A livello di file, non dentro il composable: così resta dove l'utente l'ha
 * messo anche quando si apre e si chiude un'altra schermata (Impostazioni, Notifiche...).
 */
private var posizioneFab by mutableStateOf(Offset.Zero)

/**
 * Assistente di FarmaFreelance: stesso motore del microfono ([LocalAssistente]), un comando o una
 * domanda alla volta (scrivi o parla, vedi l'esito, la risposta o la richiesta di conferma) —
 * nessuna cronologia di conversazione, per riusare esattamente la stessa logica "mostra prima,
 * esegui poi" già validata per la voce. Le domande sui propri dati ("quando è il mio prossimo
 * turno?") hanno la risposta nel popup e, se fatte a voce, anche letta ad alta voce.
 *
 * Pulsante flottante sempre visibile e trascinabile (così non copre i contenuti sotto): il
 * chiamante passa un unico [onIntent] che instrada l'intento riconosciuto alla sezione
 * competente (stesso router usato dal microfono), qualunque schermata sia visibile.
 */
@Composable
fun TextAssistantFab(
    onIntent: (rawText: String, intent: VoiceIntent) -> Unit,
    /** Nome con cui salutare l'utente nella presentazione (letto al momento del saluto). */
    nomeUtente: () -> String? = { null },
    /** true durante il tour di Farmy: niente fumetto di saluto (il tour parla già). */
    nascondiSaluto: Boolean = false
) {
    val assistente = LocalAssistente.current
    val voce = assistente.voce
    var showDialog by remember { mutableStateOf(false) }
    var saluto by remember { mutableStateOf<String?>(null) }
    val inTour by rememberUpdatedState(nascondiSaluto)
    LaunchedEffect(nascondiSaluto) { if (nascondiSaluto) saluto = null }

    // Presentazione di Farmy all'ingresso nell'app, una volta per avvio (non a ogni cambio di
    // schermata), seguita dal promemoria degli impegni. La breve attesa iniziale lascia
    // caricare il profilo (per il nome) e la schermata.
    LaunchedEffect(Unit) {
        if (presentazioneFatta) return@LaunchedEffect
        presentazioneFatta = true
        delay(1200)
        if (inTour) return@LaunchedEffect // il benvenuto lo fa il tour
        val nome = nomeUtente()?.takeIf { it.isNotBlank() }
        val presentazione = (if (nome != null) "Ciao $nome! " else "Ciao! ") +
            "Sono Farmy, il tuo agente AI. Chiedimi quello che vuoi: sono qui per aiutarti a gestire la tua giornata."
        saluto = presentazione
        voce.speak(presentazione)

        // I turni arrivano dal server qualche istante dopo l'accesso: si aspettano fino a ~6s,
        // poi il promemoria si aggiunge al fumetto e viene letto DOPO la presentazione.
        var promemoria = assistente.promemoria()
        var attese = 0
        while (promemoria == null && attese++ < 12) {
            delay(500)
            promemoria = assistente.promemoria()
        }
        if (promemoria != null && saluto != null && !inTour) {
            saluto = "$presentazione\n\n$promemoria"
            voce.speak(promemoria, accoda = true)
        }
        // Inizio mese: i pagamenti del mese scorso ancora da incassare (agenda del farmacista).
        val pagamenti = assistente.promemoriaPagamenti()
        if (pagamenti != null && saluto != null) {
            saluto = "$saluto\n\n$pagamenti"
            voce.speak(pagamenti, accoda = true)
        }
        delay(if (promemoria != null || pagamenti != null) 15_000 else 8_000)
        saluto = null
    }

    // Prima di aprire Farmy si toglie il cursore dalla pagina sotto: sul web il campo rimasto
    // attivo (es. "Inizio" del modulo turno) continuava a ricevere quello che si scriveva a Farmy.
    val focusManager = LocalFocusManager.current
    PulsanteTrascinabile(
        onClick = {
            saluto = null
            focusManager.clearFocus()
            showDialog = true
        },
        fumetto = saluto?.let { testo ->
            {
                FumettoSaluto(
                    testo = testo,
                    onClick = {
                        saluto = null
                        focusManager.clearFocus()
                        showDialog = true
                    },
                    onChiudi = {
                        saluto = null
                        voce.stop()
                    }
                )
            }
        }
    )

    if (showDialog) {
        DialogAssistente(onIntent = onIntent, onDismiss = { showDialog = false })
    }
}

/**
 * "Voce di Farmy: ..." con menu delle voci disponibili. Ogni voce toccata viene subito
 * applicata, salvata e fatta ascoltare; il menu resta aperto per confrontarle una dopo l'altra.
 */
@Composable
private fun SelettoreVoce(assistente: Assistente) {
    var aperto by remember { mutableStateOf(false) }
    var voci by remember { mutableStateOf(assistente.voce.voci()) }
    var scelta by remember { mutableStateOf(assistente.voce.voceScelta()) }

    fun scegli(id: String?) {
        scelta = id
        assistente.scegliVoce(id)
    }

    Box {
        TextButton(
            onClick = {
                voci = assistente.voce.voci() // sul web l'elenco arriva in ritardo: si rilegge all'apertura
                aperto = true
            },
            contentPadding = PaddingValues(horizontal = 4.dp)
        ) {
            Icon(AppIcons.VolumeUp, contentDescription = null, modifier = Modifier.size(18.dp))
            Spacer(modifier = Modifier.width(6.dp))
            val nome = voci.firstOrNull { it.id == scelta }?.etichetta ?: "automatica"
            Text("Voce di Farmy: $nome", style = MaterialTheme.typography.labelMedium, maxLines = 1)
        }
        DropdownMenu(expanded = aperto, onDismissRequest = { aperto = false }) {
            DropdownMenuItem(
                text = { Text("Automatica") },
                trailingIcon = { if (scelta == null) Icon(AppIcons.Check, contentDescription = "Scelta") },
                onClick = { scegli(null) }
            )
            if (voci.isEmpty()) {
                DropdownMenuItem(text = { Text("Nessun'altra voce disponibile") }, onClick = {}, enabled = false)
            }
            voci.forEach { v ->
                DropdownMenuItem(
                    text = { Text(v.etichetta) },
                    trailingIcon = { if (v.id == scelta) Icon(AppIcons.Check, contentDescription = "Scelta") },
                    onClick = { scegli(v.id) }
                )
            }
        }
    }
}

/**
 * "Promemoria di Farmy": un interruttore per ogni tipo, alternativa al chiederlo a voce
 * ("ricordami ogni mattina i turni della giornata"). Chiusa di default per non allungare il popup.
 */
@Composable
private fun SezionePromemoria(assistente: Assistente) {
    var aperta by remember { mutableStateOf(false) }
    TextButton(onClick = { aperta = !aperta }, contentPadding = PaddingValues(horizontal = 4.dp)) {
        TitoloSezione(AppIcons.Event, "Promemoria di Farmy", aperta)
    }
    if (!aperta) return
    val preferenze = assistente.preferenzePromemoria()
    Column(modifier = Modifier.padding(start = 8.dp)) {
        TipoPromemoria.entries.forEach { tipo ->
            val disponibile = assistente.promemoriaLocali || !tipo.soloAndroid
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    tipo.descrizione.replaceFirstChar { it.uppercase() } + if (disponibile) "" else " (solo app Android)",
                    style = MaterialTheme.typography.bodySmall,
                    color = if (disponibile) Color.Unspecified else Color.Gray,
                    modifier = Modifier.weight(1f)
                )
                Switch(
                    checked = preferenze.attivo(tipo),
                    onCheckedChange = { assistente.cambiaPromemoria(tipo, it) },
                    enabled = disponibile
                )
            }
        }
        Text(
            "Puoi anche dirmelo: \"ricordami ogni mattina alle 7 i turni della giornata\".",
            style = MaterialTheme.typography.labelSmall,
            color = Color.Gray
        )
    }
}

/**
 * "Avvisi turni": le ricerche salvate con "avvisami quando…", con il cestino per cancellarle.
 * Chiusa di default; all'apertura si ricarica dal server.
 */
@Composable
private fun SezioneAvvisi(esecutore: EsecutoreAzioni) {
    var aperta by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    TextButton(onClick = { aperta = !aperta }, contentPadding = PaddingValues(horizontal = 4.dp)) {
        TitoloSezione(AppIcons.Notifications, "Avvisi turni", aperta)
    }
    if (!aperta) return
    LaunchedEffect(Unit) { esecutore.ricaricaAvvisi() }
    Column(modifier = Modifier.padding(start = 8.dp)) {
        if (esecutore.avvisi.isEmpty()) {
            Text(
                "Nessun avviso. Dimmi per esempio \"avvisami quando c'è un turno a Roma il sabato\".",
                style = MaterialTheme.typography.labelSmall, color = Color.Gray
            )
        }
        esecutore.avvisi.forEach { avviso ->
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(avviso.descrizione.replaceFirstChar { it.uppercase() }, style = MaterialTheme.typography.bodySmall, modifier = Modifier.weight(1f))
                IconButton(onClick = { avviso.id?.let { id -> scope.launch { esecutore.cancellaAvviso(id) } } }, modifier = Modifier.size(28.dp)) {
                    Icon(AppIcons.Delete, contentDescription = "Cancella avviso", tint = Color.Gray, modifier = Modifier.size(18.dp))
                }
            }
        }
    }
}

/**
 * Titolo di una sezione apribile: freccia, icona e testo. Icone vere e non emoji o simboli
 * (▸ ⏰ 🔔): sul web l'app non ha un carattere con le emoji e diventerebbero quadratini.
 */
@Composable
private fun TitoloSezione(icona: androidx.compose.ui.graphics.vector.ImageVector, testo: String, aperta: Boolean) {
    Icon(
        AppIcons.ArrowDropDown, contentDescription = null,
        modifier = Modifier.size(20.dp).rotate(if (aperta) 0f else -90f)
    )
    Icon(icona, contentDescription = null, modifier = Modifier.size(16.dp))
    Spacer(modifier = Modifier.width(6.dp))
    Text(testo, style = MaterialTheme.typography.labelMedium)
}

/** true dopo la prima presentazione di questo avvio dell'app (vedi [TextAssistantFab]). */
private var presentazioneFatta = false

/** Fumetto del saluto sopra Farmy: toccandolo si apre l'assistente. */
@Composable
private fun FumettoSaluto(testo: String, onClick: () -> Unit, onChiudi: () -> Unit) {
    Card(
        onClick = onClick,
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 6.dp),
        modifier = Modifier.widthIn(max = 280.dp)
    ) {
        Row(modifier = Modifier.padding(start = 14.dp, top = 10.dp, bottom = 12.dp, end = 4.dp), verticalAlignment = Alignment.Top) {
            Text(testo, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f, fill = false).padding(top = 2.dp))
            IconButton(onClick = onChiudi, modifier = Modifier.size(28.dp)) {
                Text("X", style = MaterialTheme.typography.labelLarge, color = Color.Gray)
            }
        }
    }
}

/**
 * Posiziona il fumetto sopra il pulsante, allineato al bordo dello schermo su cui si trova il
 * pulsante (destra o sinistra, visto che si può trascinare), senza mai uscire dallo schermo.
 */
private class PosizioneFumetto(private val distanza: Int) : PopupPositionProvider {
    override fun calculatePosition(
        anchorBounds: IntRect,
        windowSize: IntSize,
        layoutDirection: LayoutDirection,
        popupContentSize: IntSize
    ): IntOffset {
        val aDestra = anchorBounds.center.x > windowSize.width / 2
        val x = if (aDestra) anchorBounds.right - popupContentSize.width else anchorBounds.left
        val y = anchorBounds.top - popupContentSize.height - distanza
        return IntOffset(
            x.coerceIn(0, (windowSize.width - popupContentSize.width).coerceAtLeast(0)),
            y.coerceAtLeast(0)
        )
    }
}

/**
 * Aspetto dell'assistente (pulsante e intestazione del popup): la mascotte Farmy, la "F"
 * sorridente ritagliata dal logo (drawable/farmyno00.png, sfondo trasparente).
 */
@Composable
internal fun AvatarAssistente(modifier: Modifier = Modifier) {
    Image(painterResource(Res.drawable.farmyno00), contentDescription = "Farmy, l'assistente", modifier = modifier)
}

/**
 * FAB che si può trascinare ovunque nell'area dello Scaffold; al rilascio si aggancia al bordo
 * sinistro o destro più vicino, come le "bolle" delle app di chat. Un tocco senza trascinare
 * resta un normale click.
 */
@Composable
private fun PulsanteTrascinabile(onClick: () -> Unit, fumetto: (@Composable () -> Unit)? = null) {
    val density = LocalDensity.current
    val scope = rememberCoroutineScope()

    // Nello slot FAB lo Scaffold passa vincoli "larghi" grandi quanto tutta l'area: sono i
    // limiti entro cui il pulsante può muoversi.
    BoxWithConstraints {
        // Pulsante con dentro la faccina e il nome "Farmy".
        val (dimensione, altezza) = with(density) { 68.dp.toPx() to 68.dp.toPx() }
        val (margine, spazioInAlto) = with(density) { 16.dp.toPx() to 96.dp.toPx() }
        // Lo Scaffold lo posiziona in basso a destra con 16dp di margine: da lì può andare a
        // sinistra fino al margine opposto e in alto fino sotto la barra del titolo.
        val minX = -(constraints.maxWidth - dimensione - 2 * margine).coerceAtLeast(0f)
        val minY = -(constraints.maxHeight - altezza - margine - spazioInAlto).coerceAtLeast(0f)

        val x = remember { Animatable(posizioneFab.x.coerceIn(minX, 0f)) }
        val y = remember { Animatable(posizioneFab.y.coerceIn(minY, 0f)) }

        // Pulsante e nome si spostano insieme: si trascina toccando l'uno o l'altro.
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier
                .offset { IntOffset(x.value.roundToInt(), y.value.roundToInt()) }
                .pointerInput(minX, minY) {
                    detectDragGestures(
                        onDrag = { change, delta ->
                            change.consume()
                            scope.launch {
                                x.snapTo((x.value + delta.x).coerceIn(minX, 0f))
                                y.snapTo((y.value + delta.y).coerceIn(minY, 0f))
                            }
                        },
                        onDragEnd = {
                            val bordo = if (x.value < minX / 2) minX else 0f
                            posizioneFab = Offset(bordo, y.value)
                            scope.launch { x.animateTo(bordo) }
                        }
                    )
                }
        ) {
            FloatingActionButton(
                onClick = onClick,
                // Bianco: Farmy è verde/blu, su un fondo colorato si perderebbe.
                containerColor = Color.White,
                // Bordo nel blu del logo: su sfondi chiari il pulsante bianco altrimenti si confonde.
                modifier = Modifier.size(68.dp).border(3.dp, FarmaFreelanceBlue, FloatingActionButtonDefaults.shape)
                    .tourBersaglio(BersaglioTour.FARMY)
            ) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    AvatarAssistente(Modifier.size(38.dp))
                    Text(
                        "Farmy",
                        fontSize = 11.sp,
                        lineHeight = 12.sp,
                        fontWeight = FontWeight.Bold,
                        color = FarmaFreelanceBlue
                    )
                }
                // Dentro il FAB: il Popup si ancora ai suoi limiti, quindi segue anche il trascinamento.
                if (fumetto != null) {
                    val distanza = with(density) { 8.dp.roundToPx() }
                    Popup(popupPositionProvider = remember(distanza) { PosizioneFumetto(distanza) }) {
                        fumetto()
                    }
                }
            }
        }
    }
}

/**
 * Il popup di Farmy. [argomento] = aperto già su un tema (es. "Chiedi a Farmy" da una notifica):
 * il titolo mostrato come domanda e la risposta/le scelte preparate da [EsecutoreAzioni].
 */
@Composable
internal fun DialogAssistente(
    onIntent: (rawText: String, intent: VoiceIntent) -> Unit,
    onDismiss: () -> Unit,
    argomento: Pair<String, suspend () -> EsitoAzione>? = null
) {
    val assistente = LocalAssistente.current
    val voce = assistente.voce
    val microfono = rememberVoiceRecognizer()
    val scope = rememberCoroutineScope()

    var testo by remember { mutableStateOf("") }
    var feedback by remember { mutableStateOf<String?>(null) }
    // Ultima domanda fatta e relativa risposta, mostrate nel popup stesso.
    var risposta by remember { mutableStateOf<Pair<String, String>?>(argomento?.let { it.first to "Sto controllando…" }) }
    // Tipo dell'ultima domanda: dopo i guadagni si offre di aprire la schermata "I miei guadagni".
    var ultimoTipo by remember { mutableStateOf<TipoDomanda?>(null) }
    // Pulsanti offerti da Farmy sotto la risposta (es. "Proponi a Laura Rossi").
    var scelte by remember { mutableStateOf<List<SceltaAzione>>(emptyList()) }
    // Domande pronte da toccare: all'apertura, dopo una risposta e quando Farmy non ha capito.
    var suggerimenti by remember { mutableStateOf(if (argomento == null) assistente.suggerimentiIniziali() else emptyList()) }

    LaunchedEffect(argomento) {
        val (titolo, prepara) = argomento ?: return@LaunchedEffect
        val e = prepara()
        risposta = titolo to e.testo
        scelte = e.scelte
    }
    var inAscolto by remember { mutableStateOf(false) }
    // Sul web il cursore va subito nel campo di Farmy; su Android no, per non coprire la finestra con la tastiera.
    val focoTesto = remember { FocusRequester() }
    LaunchedEffect(Unit) {
        if (getPlatform().name.startsWith("Web")) {
            delay(100) // il campo deve essere già sullo schermo
            runCatching { focoTesto.requestFocus() }
        }
    }

    fun chiudi() {
        voce.stop()
        onDismiss()
    }

    /** [daVoce]: la frase è arrivata dal microfono, quindi anche la risposta va letta a voce. */
    fun invia(frase: String, daVoce: Boolean) {
        println("--- [ASSISTENTE] Invio (${if (daVoce) "voce" else "testo"}): \"$frase\" ---")
        if (frase.isBlank()) return
        scelte = emptyList()
        suggerimenti = emptyList()
        val esecutore = assistente.esecutore
        // "sì"/"no" a un'azione in attesa (es. "Vuoi accettare il turno con…?"): la si esegue o annulla.
        if (esecutore != null && esecutore.eRispostaConferma(frase)) {
            risposta = frase to "…"
            feedback = null
            scope.launch {
                val e = esecutore.rispondiConferma(frase)
                risposta = frase to e.testo
                if (daVoce) voce.speak(e.testo)
                if (e.chiudi) chiudi()
            }
            testo = ""
            return
        }
        esecutore?.dimenticaAzioneInAttesa() // una frase diversa annulla la richiesta di conferma
        val esito = assistente.elabora(frase)
        println("--- [ASSISTENTE] Testo: \"$frase\" -> $esito ---")
        when (esito) {
            // Resta aperto: la risposta si legge (e si sente) qui, e si può subito chiedere altro.
            is RispostaAgente.Domanda -> {
                ultimoTipo = esito.tipo
                risposta = frase to "Sto controllando…"
                feedback = null
                scope.launch {
                    val r = assistente.rispondi(esito.tipo, frase)
                    risposta = frase to r
                    suggerimenti = assistente.suggerimentiSeguito(esito.tipo)
                    if (daVoce) voce.speak(r)
                }
            }
            // "ciao", "grazie", "non so cosa chiederti": Farmy risponde come un amico.
            is RispostaAgente.Chiacchiera -> {
                ultimoTipo = null
                val (r, domande) = assistente.chiacchiera(esito.tipo)
                risposta = frase to r
                suggerimenti = domande
                feedback = null
                if (daVoce) voce.speak(r)
            }
            // Resta aperto: la conferma ("Fatto! Ti ricorderò…") si legge qui.
            is RispostaAgente.ImpostaPromemoria -> {
                val r = assistente.impostaPromemoria(esito.impostazione)
                risposta = frase to r
                feedback = null
                if (daVoce) voce.speak(r)
            }
            // Azioni nell'app: Farmy descrive cosa farà e aspetta il "sì" (o esegue subito se innocua).
            is RispostaAgente.Azione -> {
                ultimoTipo = null
                risposta = frase to "Sto controllando…"
                feedback = null
                scope.launch {
                    val e = esecutore?.prepara(esito.azione) ?: EsitoAzione("Non posso farlo da qui.")
                    risposta = frase to e.testo
                    scelte = e.scelte
                    if (daVoce) voce.speak(e.testo)
                    if (e.chiudi) chiudi()
                }
            }
            // Resta aperto: Farmy propone le domande che forse si intendevano, da toccare.
            is RispostaAgente.NonCapito -> {
                ultimoTipo = null
                val (r, domande) = assistente.nonCapito(frase)
                risposta = frase to r
                suggerimenti = domande
                feedback = null
                if (daVoce) voce.speak(r)
            }
            // Si chiude subito: il risultato/la richiesta di conferma è già visibile nella
            // sezione di destinazione (Snackbar o risultati a schermo), il popup non deve
            // restare sopra a nasconderla.
            is RispostaAgente.Comando -> {
                onIntent(frase, esito.intent)
                chiudi()
            }
        }
        testo = ""
    }

    fun ascolta() {
        voce.stop() // il microfono non deve sentire la voce dell'assistente
        inAscolto = true
        feedback = null
        microfono.startListening(
            onResult = { frase ->
                inAscolto = false
                invia(frase, daVoce = true)
            },
            onError = { msg ->
                inAscolto = false
                feedback = msg
            }
        )
    }

    Dialog(onDismissRequest = ::chiudi) {
        Card(shape = RoundedCornerShape(16.dp)) {
            Column(modifier = Modifier.padding(20.dp).width(320.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    AvatarAssistente(Modifier.size(36.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Farmy, l'assistente", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                }
                Spacer(modifier = Modifier.height(12.dp))
                Text(
                    "Parlami come vuoi, scrivendo o con il microfono: se non capisco, ti propongo io le domande giuste.",
                    style = MaterialTheme.typography.bodySmall,
                    color = Color.Gray
                )
                risposta?.let { (domanda, testoRisposta) ->
                    Spacer(modifier = Modifier.height(12.dp))
                    Text("\"$domanda\"", style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                    Row(verticalAlignment = Alignment.Top) {
                        Text(testoRisposta, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f).padding(top = 4.dp))
                        if (voce.isAvailable()) {
                            IconButton(onClick = { voce.speak(testoRisposta) }) {
                                Icon(AppIcons.VolumeUp, contentDescription = "Ascolta la risposta")
                            }
                        }
                    }
                    scelte.forEach { scelta ->
                        OutlinedButton(
                            onClick = {
                                scelte = emptyList()
                                risposta = scelta.etichetta to "…"
                                scope.launch {
                                    val e = scelta.esegui()
                                    risposta = scelta.etichetta to e.testo
                                    scelte = e.scelte
                                }
                            },
                            modifier = Modifier.fillMaxWidth().padding(top = 4.dp)
                        ) { Text(scelta.etichetta) }
                    }
                    if (ultimoTipo == TipoDomanda.GUADAGNI && !testoRisposta.endsWith("per i farmacisti.")) {
                        TextButton(onClick = {
                            onIntent(domanda, VoiceIntent.NavigaA(VoiceIntent.Sezione.GUADAGNI))
                            chiudi()
                        }) { Text("€ Apri i miei guadagni") }
                    }
                    if (ultimoTipo == TipoDomanda.AGENDA) {
                        TextButton(onClick = {
                            onIntent(domanda, VoiceIntent.NavigaA(VoiceIntent.Sezione.AGENDA))
                            chiudi()
                        }) {
                            Icon(AppIcons.Event, contentDescription = null, modifier = Modifier.size(18.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text("Apri l'agenda dei turni")
                        }
                    }
                }
                if (suggerimenti.isNotEmpty() && scelte.isEmpty()) {
                    Spacer(modifier = Modifier.height(10.dp))
                    Text(
                        if (risposta == null) "Prova a chiedermi:" else "Puoi chiedermi anche:",
                        style = MaterialTheme.typography.labelMedium,
                        color = Color.Gray
                    )
                    suggerimenti.forEach { domanda ->
                        SuggestionChip(
                            onClick = { invia(domanda, daVoce = false) },
                            label = { Text(domanda) },
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
                Spacer(modifier = Modifier.height(12.dp))
                OutlinedTextField(
                    value = testo,
                    onValueChange = { testo = it },
                    modifier = Modifier.fillMaxWidth().focusRequester(focoTesto),
                    label = { Text(if (inAscolto) "In ascolto..." else "Comando o domanda") },
                    singleLine = true,
                    trailingIcon = if (microfono.isAvailable()) {
                        {
                            IconButton(onClick = ::ascolta, enabled = !inAscolto) {
                                Icon(
                                    AppIcons.Mic,
                                    contentDescription = "Parla con l'assistente",
                                    // Sempre rosso per farsi notare; mentre ascolta pulsa.
                                    tint = Color(0xFFD32F2F).copy(alpha = 1f - 0.6f * rememberLampeggio(inAscolto))
                                )
                            }
                        }
                    } else null,
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Send),
                    keyboardActions = KeyboardActions(onSend = { invia(testo, daVoce = false) })
                )
                if (feedback != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(feedback!!, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.primary)
                }
                if (voce.isAvailable()) {
                    Spacer(modifier = Modifier.height(4.dp))
                    SelettoreVoce(assistente)
                }
                SezionePromemoria(assistente)
                assistente.esecutore?.let { SezioneAvvisi(it) }
                Spacer(modifier = Modifier.height(8.dp))
                Text(
                    "Suggerimento: puoi trascinare il pulsante dell'assistente dove non ti dà fastidio.",
                    style = MaterialTheme.typography.labelSmall,
                    color = Color.Gray
                )
                Spacer(modifier = Modifier.height(12.dp))
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = ::chiudi) { Text("Chiudi") }
                    Spacer(modifier = Modifier.width(8.dp))
                    Button(onClick = { invia(testo, daVoce = false) }) { Text("Invia") }
                }
            }
        }
    }
}
